import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useTour } from '@/contexts/TourContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  CalendarClock, 
  AlertCircle, 
  ChevronDown, 
  ChevronUp,
  AlertTriangle,
  Clock,
  MapPin
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function DashboardOverlay({ 
  onOpenPlannedChanges,
  onOpenTickets,
  onOpenRiskExposure,
  currentBuilding,
  onNavigate
}) {
  const { token } = useAuth();
  const { currentTour } = useTour();
  const [stats, setStats] = useState(null);
  const [buildingChanges, setBuildingChanges] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(false);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    if (!currentTour?.id || !token) return;
    
    setLoading(true);
    try {
      const [changesRes, buildingsRes, ticketsRes, risksRes] = await Promise.all([
        fetch(`${API_URL}/api/planned-changes?tour_id=${currentTour.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/buildings?tour_id=${currentTour.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/tickets?tour_id=${currentTour.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/risks?tour_id=${currentTour.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      const changes = changesRes.ok ? await changesRes.json() : [];
      const buildings = buildingsRes.ok ? await buildingsRes.json() : [];
      const tickets = ticketsRes.ok ? await ticketsRes.json() : [];
      const risks = risksRes.ok ? await risksRes.json() : [];

      // Calculate stats
      const activeChanges = changes.filter(c => 
        ['proposed', 'under_review', 'approved', 'in_progress'].includes(c.status)
      );
      const highRiskChanges = changes.filter(c => 
        c.impact_risk?.risk_level === 'high' && 
        ['proposed', 'under_review', 'approved', 'in_progress'].includes(c.status)
      );
      const openTickets = tickets.filter(t => t.status === 'open' || t.status === 'in_progress');
      
      // Risk stats
      const activeRisks = risks.filter(r => 
        ['identified', 'escalating'].includes(r.status)
      );
      const criticalRisks = risks.filter(r => 
        r.assessment?.risk_rating === 'critical' && 
        ['identified', 'escalating'].includes(r.status)
      );
      const highRisks = risks.filter(r => 
        r.assessment?.risk_rating === 'high' && 
        ['identified', 'escalating'].includes(r.status)
      );

      setStats({
        totalChanges: changes.length,
        activeChanges: activeChanges.length,
        highRiskChanges: highRiskChanges.length,
        buildingsCount: buildings.length,
        openTickets: openTickets.length,
        activeRisks: activeRisks.length,
        criticalRisks: criticalRisks.length,
        highRisks: highRisks.length,
        changes,
        buildings,
        risks
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  }, [currentTour?.id, token]);

  // Update building-specific changes when currentBuilding changes
  useEffect(() => {
    if (stats?.changes && currentBuilding) {
      const relatedChanges = stats.changes.filter(c => 
        c.building_id === currentBuilding.id &&
        ['proposed', 'under_review', 'approved', 'in_progress'].includes(c.status)
      );
      setBuildingChanges(relatedChanges);
    } else {
      setBuildingChanges([]);
    }
  }, [currentBuilding, stats?.changes]);

  useEffect(() => {
    fetchStats();
    // Refresh every 60 seconds
    const interval = setInterval(fetchStats, 60000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  if (!stats || loading) return null;

  const hasAlerts = stats.highRiskChanges > 0 || stats.openTickets > 0 || stats.criticalRisks > 0;

  return (
    <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 pointer-events-auto">
      {/* Main Dashboard Bar */}
      <div className="bg-background/95 backdrop-blur-sm border rounded-lg shadow-lg">
        <div className="flex items-center gap-1 p-2">
          {/* Planned Changes */}
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-3 gap-2"
            onClick={onOpenPlannedChanges}
            data-testid="dashboard-planned-changes-btn"
          >
            <CalendarClock className="h-4 w-4 text-blue-500" />
            <span className="text-sm font-medium">{stats.activeChanges}</span>
            <span className="text-xs text-muted-foreground hidden sm:inline">Planned Changes</span>
            {stats.highRiskChanges > 0 && (
              <Badge variant="destructive" className="h-5 px-1.5 text-xs">
                {stats.highRiskChanges} High Risk
              </Badge>
            )}
          </Button>

          <div className="w-px h-6 bg-border" />

          {/* Active Issues */}
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-3 gap-2"
            onClick={onOpenTickets}
            data-testid="dashboard-active-issues-btn"
          >
            <AlertCircle className="h-4 w-4 text-orange-500" />
            <span className="text-sm font-medium">{stats.openTickets}</span>
            <span className="text-xs text-muted-foreground hidden sm:inline">Active Issues</span>
          </Button>

          <div className="w-px h-6 bg-border" />

          {/* Risk & Exposure */}
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-3 gap-2"
            onClick={onOpenRiskExposure}
            data-testid="dashboard-risk-exposure-btn"
          >
            <AlertTriangle className="h-4 w-4 text-red-500" />
            <span className="text-sm font-medium">{stats.activeRisks}</span>
            <span className="text-xs text-muted-foreground hidden sm:inline">Risk & Exposure</span>
            {(stats.criticalRisks > 0 || stats.highRisks > 0) && (
              <Badge variant="destructive" className="h-5 px-1.5 text-xs">
                {stats.criticalRisks > 0 ? `${stats.criticalRisks} Critical` : `${stats.highRisks} High`}
              </Badge>
            )}
          </Button>

          {/* Expand/Collapse for Building Details */}
          {currentBuilding && buildingChanges.length > 0 && (
            <>
              <div className="w-px h-6 bg-border" />
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2"
                onClick={() => setIsExpanded(!isExpanded)}
              >
                {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </>
          )}
        </div>

        {/* Current Building Info */}
        {currentBuilding && (
          <div className="px-3 py-2 border-t bg-muted/50">
            <div className="flex items-center gap-2">
              <MapPin className="h-3 w-3 text-primary" />
              <span className="text-xs font-medium">{currentBuilding.name}</span>
              {buildingChanges.length > 0 && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  {buildingChanges.length} Planned Change{buildingChanges.length > 1 ? 's' : ''}
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Expanded Building Changes List */}
        {isExpanded && buildingChanges.length > 0 && (
          <div className="border-t max-h-48 overflow-y-auto">
            {buildingChanges.map(change => (
              <div 
                key={change.id}
                className="px-3 py-2 hover:bg-accent/50 cursor-pointer border-b last:border-b-0"
                onClick={() => {
                  if (change.sweep_id && onNavigate) {
                    onNavigate(change.sweep_id, {
                      rotation: change.rotation,
                      transitionTime: 1500
                    });
                  }
                }}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-muted-foreground">{change.change_id}</span>
                      <Badge 
                        variant="outline" 
                        className={`text-xs ${
                          change.impact_risk?.risk_level === 'high' ? 'border-red-500 text-red-500' :
                          change.impact_risk?.risk_level === 'medium' ? 'border-yellow-500 text-yellow-500' :
                          'border-green-500 text-green-500'
                        }`}
                      >
                        {change.impact_risk?.risk_level || 'low'}
                      </Badge>
                    </div>
                    <p className="text-sm font-medium truncate">{change.title}</p>
                    {change.proposed_start_date && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                        <Clock className="h-3 w-3" />
                        {new Date(change.proposed_start_date).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  {change.sweep_id && (
                    <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Alert Indicator */}
      {hasAlerts && (
        <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse" />
      )}
    </div>
  );
}

export default DashboardOverlay;
